import React from "react";
import { useL } from "@/lib/useL";

const Help = ({ en, ...fr }) => {
  const L = useL();
  const { title, what, examples, cost } = L(fr, en);
  return (
  <div className="space-y-2">
    <div className="font-bold text-[14px]">{title}</div>
    <p>{what}</p>
    <ul className="list-disc pl-5 space-y-0.5">
      {examples.map((e) => <li key={e}>{e}</li>)}
    </ul>
    <p className="opacity-85 text-[12.5px]">{cost}</p>
  </div>
  );
};

// Detailed explanations for the 4 big blue action bars (shown in their ⓘ bubble).
export const ACTION_HELP = {
  oneClick: (
    <Help
      en={{ title: "One-click action", what: "Instead of a classic summary, SUX1 applies your instruction directly to the pasted text: you get the final document in one go. Use it BEFORE running the summary.", examples: ["Turn a chapter into 5 MCQs with answers", "Write an email to parents from an article", "Simplify, rephrase, translate (12 languages) or polish the text"], cost: "Cost: like a summary (1 credit with Gemini, 2 with Claude or GPT)." }}
      title="Action en 1 clic"
      what="Au lieu d'un résumé classique, SUX1 applique directement votre consigne au texte collé : vous obtenez le document final en une seule fois. À utiliser AVANT de lancer le résumé."
      examples={[
        "Transformer un chapitre en 5 QCM avec corrigé",
        "Écrire un mail aux parents à partir d'un article",
        "Vulgariser, reformuler, traduire (12 langues) ou polir le texte",
      ]}
      cost="Coût : comme un résumé (1 crédit avec Gemini, 2 avec Claude ou GPT)."
    />
  ),
  summaryActions: (
    <Help
      en={{ title: "Summary actions", what: "Transforms the summary you just got, without going back to the original text. The result replaces the display, and you can return to the original.", examples: ["Explain it: for a 10-year-old, a teenager, a beginner or a non-native speaker", "Rephrase: more concise, more formal or simpler", "Translate into 12 languages, or polish spelling and flow"], cost: "Cost: 1 credit for 5 actions (shared with follow-up questions)." }}
      title="Actions sur le résumé"
      what="Transforme le résumé que vous venez d'obtenir, sans repartir du texte d'origine. Le résultat remplace l'affichage, et vous pouvez revenir à l'original."
      examples={[
        "Explique-moi : pour un enfant de 10 ans, un ado, un débutant ou un non-francophone",
        "Reformuler : plus concis, plus formel ou plus simple",
        "Traduire en 12 langues, ou Polir l'orthographe et la fluidité",
      ]}
      cost="Coût : 1 crédit pour 5 actions (partagé avec les questions de suivi)."
    />
  ),
  rework: (
    <Help
      en={{ title: "Rework this summary", what: "Copies this summary into the text area as if it were a new document. You can then change the length, format, class level or model, and run it again.", examples: ["Shorten a summary that is still too long", "Turn it into a DYS revision sheet or Metaphors", "Explain it as a poem for recitation"], cost: "Cost: copying is free; the new summary then costs 1 credit (Gemini)." }}
      title="Retravailler ce résumé"
      what="Recopie ce résumé dans la zone de texte, comme s'il s'agissait d'un nouveau document. Vous pouvez alors changer la longueur, le format, le niveau de classe ou le modèle, puis relancer."
      examples={[
        "Raccourcir encore un résumé trop long",
        "Le transformer en fiche de révision DYS ou en Métaphores",
        "L'expliquer en poème pour une récitation",
      ]}
      cost="Coût : gratuit pour recopier ; le nouveau résumé coûte ensuite 1 crédit (Gemini)."
    />
  ),
  followUp: (
    <Help
      en={{ title: "Follow-up question", what: "Ask a further question about this summary, or pick a ready-made request. The answer relies only on the summary and can be appended to it.", examples: ["Generate 5 MCQs with answers, or a model answer graded out of 20", "Prepare a 55-minute lesson plan or an email to parents", "Rephrase for a DYS student, or suggest 3 debate topics"], cost: "Cost: 1 credit for 5 questions (shared with summary actions)." }}
      title="Question de suivi"
      what="Posez une question complémentaire sur ce résumé, ou choisissez une demande toute prête. La réponse s'appuie uniquement sur le résumé, et peut être ajoutée à la fin de celui-ci."
      examples={[
        "Générer 5 QCM avec corrigé, ou une correction type notée sur 20",
        "Préparer un plan de séance de 55 min ou un mail aux parents",
        "Reformuler pour un élève DYS, ou proposer 3 sujets de débat",
      ]}
      cost="Coût : 1 crédit pour 5 questions (partagé avec les actions sur le résumé)."
    />
  ),
};
